import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Link2, Plus, X, Send, Music2 } from "lucide-react";
import { api, errMsg, media } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const box = "border-white/10 bg-[#07090E] focus-visible:ring-[#FF2E63]";
const filled = (v) => (Array.isArray(v) ? v.length > 0 : !!v);

const Photos = ({ value = [], max, onChange, id }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const files = [...e.target.files].slice(0, max - value.length);
    e.target.value = "";
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) { const fd = new FormData(); fd.append("file", f); urls.push((await api.post("/me/brief-upload", fd)).data.url); }
      onChange([...value, ...urls]);
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {value.map((u) => (
        <div key={u} className="group relative aspect-square overflow-hidden rounded-lg border border-white/10">
          <img src={media(u)} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => onChange(value.filter((x) => x !== u))} className="absolute right-1 top-1 rounded-full bg-black/70 p-1" data-testid={`brief-${id}-remove`}><X className="h-3 w-3" /></button>
        </div>
      ))}
      {value.length < max && (
        <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid={`brief-${id}-add`}
          className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-white/20 text-slate-500 transition-colors hover:border-[#FF2E63] hover:text-[#FF2E63]">
          <ImagePlus className="h-5 w-5" /><span className="font-mono text-[10px] uppercase">{busy ? "Uploading…" : `${value.length}/${max}`}</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={add} />
    </div>
  );
};

const Links = ({ value = [], max, onChange, id }) => {
  const list = value.length ? value : [""];
  const set = (i, v) => onChange(list.map((x, j) => (j === i ? v : x)));
  return (
    <div className="space-y-2">
      {list.map((l, i) => (
        <div key={i} className="relative">
          <Link2 className="absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
          <Input value={l} onChange={(e) => set(i, e.target.value)} placeholder="https://" className={`${box} h-11 pl-9`} data-testid={`brief-${id}-${i}`} />
        </div>
      ))}
      {list.length < max && <button type="button" onClick={() => onChange([...list, ""])} className="flex items-center gap-1 font-mono text-[11px] uppercase text-[#00F5D4]" data-testid={`brief-${id}-more`}><Plus className="h-3 w-3" />Add link</button>}
    </div>
  );
};

const Field = ({ f, v, set, tracks }) => {
  const id = f.id;
  const input = {
    text: <Input value={v || ""} onChange={(e) => set(e.target.value)} maxLength={id === "headline" ? 60 : 300} className={`${box} h-11`} data-testid={`brief-${id}`} />,
    textarea: <Textarea value={v || ""} onChange={(e) => set(e.target.value)} rows={id.startsWith("q_") ? 4 : 3} className={box} data-testid={`brief-${id}`} />,
    track: (
      <div className="grid gap-2">
        {tracks.map((t) => (
          <button type="button" key={t.id} onClick={() => set(t.id)} data-testid={`brief-track-${t.id}`}
            className={`flex items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors ${v === t.id ? "border-[#FF2E63] bg-[#FF2E63]/10" : "border-white/10 hover:border-white/30"}`}>
            <Music2 className={`h-4 w-4 ${v === t.id ? "text-[#FF2E63]" : "text-slate-500"}`} />{t.title}
            <span className="ml-auto font-mono text-[10px] uppercase text-slate-500">{t.status}</span>
          </button>
        ))}
        {!tracks.length && <p className="text-sm text-[#FFB800]">Upload a track first from the Tracks tab.</p>}
      </div>
    ),
    photos: <Photos value={v} max={f.max} onChange={set} id={id} />,
    links: <Links value={v} max={f.max} onChange={set} id={id} />,
  }[f.type];
  return (
    <label className="block space-y-2">
      <span className="flex items-baseline gap-2 font-display text-sm font-bold">{f.label}{f.required && <span className="text-[#FF2E63]">*</span>}</span>
      {f.hint && <span className="block text-xs text-slate-500">{f.hint}</span>}
      {input}
    </label>
  );
};

export const BriefDialog = ({ order, brief, name, tracks, onClose, onSaved }) => {
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (order) setV({ track: order.track_id, ...(order.brief || {}) }); }, [order]);
  if (!order || !brief) return null;
  const req = brief.fields.filter((f) => f.required);
  const done = req.filter((f) => filled(v[f.id])).length;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/me/orders/${order.id}/brief`, { answers: v });
      toast.success("Brief sent", { description: "Thanks! We'll get to work and email you when it's live." });
      onSaved(); onClose();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0F1420] p-0 text-white" data-testid="brief-dialog">
        <div className="sticky top-0 z-10 border-b border-white/10 bg-[#0F1420]/95 p-6 backdrop-blur">
          <DialogHeader>
            <span className="label-mono">Service brief</span>
            <DialogTitle className="font-display text-2xl font-black uppercase">{name}</DialogTitle>
            <DialogDescription className="text-slate-400">{brief.intro}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 h-1 overflow-hidden rounded-full bg-white/5"><div className="h-full bg-gradient-to-r from-[#FF2E63] to-[#00F5D4] transition-[width] duration-500" style={{ width: `${(done / req.length) * 100}%` }} /></div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid="brief-progress">{done}/{req.length} required</div>
        </div>
        <form onSubmit={submit} className="space-y-6 p-6">
          {brief.fields.map((f) => <Field key={f.id} f={f} v={v[f.id]} tracks={tracks} set={(x) => setV((p) => ({ ...p, [f.id]: x }))} />)}
          <button className="btn-hot w-full" disabled={busy} data-testid="brief-submit"><Send className="h-4 w-4" />{busy ? "Sending…" : order.brief ? "Update brief" : "Send brief"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
